/**
 * Display values for property_photo_url.
 * Stored values keep an object address for property-photos/<client id>/<file>.
 * Older rows store that same kind of address, a bucket-root filename
 * (agent_client_timestamp.ext), or a property-photos/ path. The bucket is
 * private, so the screen asks for a signed URL before showing the file.
 * Some objects saved before raw-byte uploads are a WebKit multipart body
 * with a 200 response, which an <img> cannot decode.
 */

const PUBLIC_PREFIX = 'storage/v1/object/public/property-photos/';
const BUCKET_PREFIX = 'property-photos/';

function propertyPhotoSrc(stored, supabaseUrl = process.env.REACT_APP_SUPABASE_URL) {
  const value = typeof stored === 'string' ? stored.trim() : '';
  if (!value) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return value;
  if (value.includes('..') || value.includes('\\') || value.includes('\0')) return '';

  let path = value.replace(/^\/+/, '');
  if (path.startsWith(PUBLIC_PREFIX)) path = path.slice(PUBLIC_PREFIX.length);
  else if (path.startsWith(BUCKET_PREFIX)) path = path.slice(BUCKET_PREFIX.length);
  path = path.replace(/^\/+/, '');
  if (!path || path.includes('..')) return '';

  const base = String(supabaseUrl || '').trim().replace(/\/$/, '');
  if (!base) return value;
  return `${base}/${PUBLIC_PREFIX}${path}`;
}

function isRecoverablePropertyPhotoUrl(url) {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname;
    return parsed.protocol === 'https:'
      && (path.includes('/object/public/property-photos/') || path.includes('/object/sign/property-photos/'))
      && !path.includes('..');
  } catch (error) {
    return false;
  }
}

function imageMime(bytes) {
  if (!bytes || bytes.length < 3) return '';
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  if (bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return 'image/png';
  }
  if (bytes.length >= 4 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
    return 'image/gif';
  }
  if (
    bytes.length >= 12
    && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
    && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    return 'image/webp';
  }
  return '';
}

function latin1Slice(bytes, start, end) {
  let text = '';
  const stop = Math.min(bytes.length, end);
  for (let i = start; i < stop; i += 1) text += String.fromCharCode(bytes[i]);
  return text;
}

function bytesFromLatin1(text) {
  const out = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i += 1) out[i] = text.charCodeAt(i) & 0xff;
  return out;
}

function indexOfBytes(haystack, needle, from = 0) {
  if (!needle.length || haystack.length < from + needle.length) return -1;
  const last = haystack.length - needle.length;
  for (let i = Math.max(0, from); i <= last; i += 1) {
    let match = true;
    for (let j = 0; j < needle.length; j += 1) {
      if (haystack[i + j] !== needle[j]) {
        match = false;
        break;
      }
    }
    if (match) return i;
  }
  return -1;
}

function firstLine(head) {
  const crlf = head.indexOf('\r\n');
  const lf = head.indexOf('\n');
  if (crlf >= 0 && (lf < 0 || crlf <= lf)) return { boundary: head.slice(0, crlf), sep: '\r\n' };
  if (lf >= 0) return { boundary: head.slice(0, lf), sep: '\n' };
  return null;
}

function imageBytesFromStoredPhoto(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input || []);
  if (imageMime(bytes)) return bytes;
  if (bytes.length < 4 || bytes[0] !== 45 || bytes[1] !== 45) return null;

  const head = latin1Slice(bytes, 0, Math.min(bytes.length, 8192));
  const line = firstLine(head);
  if (!line || line.boundary.length < 3) return null;
  const nameAt = head.indexOf('filename=');
  if (nameAt < 0) return null;
  const headerEndToken = `${line.sep}${line.sep}`;
  const headerEnd = head.indexOf(headerEndToken, nameAt);
  if (headerEnd < 0) return null;

  const start = headerEnd + headerEndToken.length;
  const closeAt = indexOfBytes(bytes, bytesFromLatin1(`${line.sep}${line.boundary}`), start);
  if (closeAt < start) return null;
  const image = bytes.subarray(start, closeAt);
  return imageMime(image) ? image : null;
}

async function recoverLegacyPropertyPhoto(url, options = {}) {
  if (!isRecoverablePropertyPhotoUrl(url)) return '';
  const fetchImpl = options.fetch || (typeof fetch === 'function' ? fetch : null);
  if (typeof fetchImpl !== 'function') return '';
  const createObjectURL = options.createObjectURL || ((blob) => (
    typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
      ? URL.createObjectURL(blob)
      : ''
  ));

  let response;
  try {
    response = await fetchImpl(url);
  } catch (error) {
    return '';
  }
  if (!response || !response.ok || typeof response.arrayBuffer !== 'function') return '';

  let buffer;
  try {
    buffer = await response.arrayBuffer();
  } catch (error) {
    return '';
  }
  const image = imageBytesFromStoredPhoto(new Uint8Array(buffer));
  const mime = image && imageMime(image);
  if (!mime) return '';
  try {
    return createObjectURL(new Blob([image], { type: mime })) || '';
  } catch (error) {
    return '';
  }
}

export {
  propertyPhotoSrc,
  isRecoverablePropertyPhotoUrl,
  imageMime,
  imageBytesFromStoredPhoto,
  recoverLegacyPropertyPhoto,
};
